import React, { useState, useCallback, useContext, useEffect } from 'react'
import { gql } from 'apollo-boost'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { Alert, Box } from '@smooth-ui/core-sc'
import { Redirect, Link } from 'react-router-dom'

import trackGMT from 'helpers/gtm'
import PageWrapper from 'components/PageWrapper/PageWrapper'
import { PageTitle, SpinnerButton, P } from 'components/Elements/index'
import SignupForm from './SignupForm'
import routeConfig from 'routeConfig'
import StripeWrapper from 'helpers/StripeWrapper'
import TokenContext from 'helpers/tokenContext'

import CouponView from './Coupon'

import config from 'config'

const name = 'Registrer deg'

const MUTATION_CREATE_PAYMENT_INTENT = gql`
  mutation createPaymentIntent {
    createPaymentIntent {
      client_secret
    }
  }
`

const MUTATION_SIGNUP = gql`
  mutation signup(
    $given_name: String!
    $family_name: String!
    $email: String!
    $password: String!
    $payment_method: String!
    $coupon: String
  ) {
    signup(
      given_name: $given_name
      family_name: $family_name
      email: $email
      password: $password
      payment_method: $payment_method
      coupon: $coupon
    ) {
      payment_intent {
        id
        client_secret
        status
      }
      setup_intent {
        id
        client_secret
        status
        payment_method
      }
    }
  }
`
const MUTATION_LOGIN = gql`
  mutation login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      access_token
      user {
        membership_level
      }
    }
  }
`
const QUERY_PLAN = gql`
  query Plan {
    plan {
      id
      amount
      interval
      interval_count
      nickname
      trial_period_days
      billing_scheme
      livemode
    }
  }
`
const intervals = {
  day: 'dag',
  week: 'uke',
  month: 'måned'
}
const PlanInfo = ({ coupon }) => {
  const { data, loading } = useQuery(QUERY_PLAN)
  if (loading) {
    return null
  }
  const {
    nickname,
    trial_period_days,
    interval,
    interval_count,
    amount
  } = data.plan
  let desc = ``
  const rabatt = coupon && coupon.amount_off ? coupon : null
  if (rabatt) {
    const rabattAmount = amount - rabatt.amount_off
    const rabattLength = rabatt.duration_in_months
      ? `i ${rabatt.duration_in_months} måneder`
      : 'per mnd'
    if (rabattAmount) {
      desc = `${
        rabatt.duration_in_months ? 'Prøv for kun' : 'Kun'
      } kr. ${rabattAmount / 100},- ${rabattLength}`
    } else {
      desc = `Prøv gratis i ${rabattLength}`
    }
  } else {
    desc = `Prøv gratis i ${trial_period_days} dager`
  }
  if (rabatt && !rabatt.duration_in_months) {
    desc = desc + '.'
  } else {
    desc =
      desc +
      `, deretter kr. ${amount / 100},- / ${
        interval_count > 1 ? interval_count : ''
      } ${intervals[interval]}.`
  }

  return (
    <P mb={3}>
      <strong>{rabatt ? coupon.name : nickname}:</strong> {desc}
    </P>
  )
}

const Signup = ({
  history: { replace },
  location: { search },
  match: { params }
}) => {
  const [values, setValues] = useState(null)
  const [coupon, setCoupon] = useState(params.coupon)
  const [postRegistration, setPostRegistration] = useState({
    loginReady: false,
    hasRegistered: false
  })
  const [signup, { data, loading, error: signupError }] = useMutation(
    MUTATION_SIGNUP
  )
  const [createPaymentIntent, { data: paymentIntent }] = useMutation(
    MUTATION_CREATE_PAYMENT_INTENT
  )
  useEffect(() => {
    createPaymentIntent()
  }, [createPaymentIntent])
  const [login, { data: loginData }] = useMutation(MUTATION_LOGIN)
  const { token, setToken } = useContext(TokenContext)
  const onSubmit = async variables => {
    console.log('submit')
    if (loading) {
      return trackGMT({
        event: 'custom',
        category: 'Signup',
        action: 'Form submit',
        label: 'Dublicate stopped'
      })
    }
    setValues(variables)
    try {
      await signup({
        variables: {
          ...variables,
          coupon: coupon && coupon.id
        }
      })
      trackGMT({
        event: 'custom',
        category: 'Signup',
        action: 'Form submit',
        label: 'Success'
      })
    } catch (error) {
      trackGMT({
        event: 'custom',
        category: 'Signup',
        action: 'Form submit',
        label: error.message || error
      })
    }
  }
  const onComplete = useCallback(
    payload => {
      setPostRegistration({
        loginReady: false,
        hasRegistered: true
      })
      trackGMT({
        event: 'custom',
        category: 'Signup',
        action: 'Form submit ok'
      })
      async function check(count = 0) {
        if (count > 5) {
          return setPostRegistration({
            loginReady: false,
            hasRegistered: true,
            timeout: true
          })
        }
        const { data } = await login({
          variables: values
        })
        console.log('check', data, count)
        if (data.login.user.membership_level > 0) {
          trackGMT({
            event: 'custom',
            category: 'Signup',
            action: 'User ready'
          })
          return setPostRegistration({
            loginReady: true,
            hasRegistered: true
          })
        }
        setTimeout(check, 500, count + 1)
      }
      check()
    },
    [values, login]
  )
  const performLogin = async () => {
    trackGMT({
      event: 'custom',
      category: 'Signup',
      action: 'User login inn'
    })
    await setToken(loginData.login.access_token)
  }
  if (token) {
    return <Redirect to={routeConfig.home} />
  }
  const error = signupError
  return (
    <PageWrapper title={name}>
      <Box width={{ sm: 1, md: 0.5 }} mx="auto">
        <PageTitle color="primary" fontSize="2.6rem">
          {name}
        </PageTitle>
        <P fontSize="1.2rem">
          <strong>
            Pilates, yoga, barre, Effektiv trening hjemme, tren online, Live
            klasser, kurs og over 500 treningsvideoer
          </strong>
        </P>
        <P>
          Registrer deg ved å fylle inn feltene under. Etter godkjent betaling,
          kan du logge inn med ditt valgte passord og brukernavn og komme i gang
          med treningen med en gang. Kundedata blir ikke delt med/solgt til en
          tredje part.
        </P>
        <P mb={3}>
          Ved å klikke "Registrer deg" godtar du{' '}
          <Link
            to={config.agreementUrl}
            style={{ textDecoration: 'underline' }}
          >
            våre retningslinjer for bruk
          </Link>
        </P>
        <StripeWrapper>
          {postRegistration.hasRegistered ? (
            <Box mb={3}>
              <Alert variant="success">
                <strong>Takk for at du registrerte deg!</strong>{' '}
                {postRegistration.loginReady
                  ? 'Din bruker er nå klar, og du kan logge inn ved å trykke under'
                  : 'Vent litt mens vi sjekker at ditt betalingskort er i orden'}
              </Alert>
              {postRegistration.timeout ? (
                <Box>
                  Obs, dette tok vist tid, men vi sender deg en e-post når du
                  kan logge inn.
                </Box>
              ) : (
                <SpinnerButton
                  working={!postRegistration.loginReady}
                  onClick={performLogin}
                  disabled={!postRegistration.loginReady}
                  text="Kom igang nå!"
                  textWorking="Vent litt.."
                />
              )}
            </Box>
          ) : (
            <SignupForm
              title={<PlanInfo coupon={coupon} />}
              onSubmit={onSubmit}
              error={error && fixError(error)}
              loading={loading}
              payload={data && data.signup ? data.signup : null}
              onComplete={onComplete}
              clientSecret={
                paymentIntent
                  ? paymentIntent.createPaymentIntent.client_secret
                  : null
              }
            >
              <CouponView coupon={coupon} setCoupon={setCoupon} />
            </SignupForm>
          )}
        </StripeWrapper>
      </Box>
    </PageWrapper>
  )
}

const fixError = error => {
  if (error.message.includes('Network error')) {
    return 'Nettverksfeil, vennligst forsøk på nytt'
  }
  return error.message.replace('GraphQL error: ', '')
}

export default Signup
